import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Moon, Sun, MonitorCog } from "lucide-react";
import { Button, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@erp/ui";

import { getStoredTheme, setTheme, type Theme } from "@/lib/theme";

const ORDER: Theme[] = ["light", "dark", "system"];

const ICONS: Record<Theme, typeof Sun> = {
  light: Sun,
  dark: Moon,
  system: MonitorCog,
};

/**
 * Compact theme cycle button (light → dark → system). Used on the auth pages
 * where the full topbar dropdown is not available.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const { t } = useTranslation();
  const [theme, setThemeState] = useState<Theme>(() => getStoredTheme());

  const next = ORDER[(ORDER.indexOf(theme) + 1) % ORDER.length] as Theme;
  const Icon = ICONS[theme];
  const label = t("theme.cycle", "Theme: {{current}} — switch to {{next}}", {
    current: t(`theme.${theme}`),
    next: t(`theme.${next}`),
  });

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={label}
            className={className}
            onClick={() => {
              setTheme(next);
              setThemeState(next);
            }}
          >
            <Icon className="size-4" aria-hidden="true" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
